import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Grid } from "@mui/material";
import { Icon } from "@iconify/react";
import Layout from "../../../components/Layout";
import PageTitle from "../../../components/PageTitle";
import Button from "../../../components/Button";
import Table from "../../../components/Table/Table";
import Modal from "../../../components/Modal";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import BreadCrumb from "../../../components/BreadCrumb";
import useStyles from "../styles";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";
import useValidator from "../../../hooks/useValidator";

const SYLLABUS_ENDPOINT = "/api/v1/syllabus";
const SUBJECTS_ENDPOINT = "/api/v1/subjects";
const SESSION_ENDPOINT = "/api/v1/school-plan/session";
const TERM_ENDPOINT = "/api/v1/school-plan/terms";
const CLASSES_ENDPOINT = "/api/v1/class";

const EmployerSyllabusDetails = () => {
  const { id: subjectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classes = useStyles();

  const [subjectName, setSubjectName] = useState("");
  const [sessions, setSessions] = useState([]);
  const [allTerms, setAllTerms] = useState([]);
  const [classList, setClassList] = useState([]);

  const [searchResult, setSearchResult] = useState([]);
  const [hasAnySyllabus, setHasAnySyllabus] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const [filters] = useState({
    session_id: "",
    term_id: "",
    class_id: searchParams.get("class_id") || ""
  });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [classSearch, setClassSearch] = useState("");
  const [createForm, setCreateForm] = useState({ session_id: "", term_id: "", class_id: [] });
  const [createValidator, showCreateValidator] = useValidator();

  const getSubject = () => {
    httprequest.get(`${SUBJECTS_ENDPOINT}/${subjectId}`)
      .then((res) => setSubjectName(res?.data?.data?.name))
      .catch(() => {});
  };

  const getSessions = () => {
    httprequest.get(SESSION_ENDPOINT)
      .then((res) => setSessions(res?.data?.data || []))
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to load sessions"));
  };

  const getTerms = () => {
    httprequest.get(TERM_ENDPOINT)
      .then((res) => setAllTerms(res?.data?.data || []))
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to load terms"));
  };

  const getClasses = () => {
    httprequest.get(CLASSES_ENDPOINT)
      .then((res) => setClassList(res?.data?.data || []))
      .catch(() => {});
  };

  const getSyllabusBaseline = () => {
    httprequest.get(`${SYLLABUS_ENDPOINT}/subjects/${subjectId}`, { params: { limit: 1, page: 1 } })
      .then((res) => setHasAnySyllabus((res?.data?.data?.items || []).length > 0))
      .catch(() => {});
  };

  const getSyllabusList = () => {
    setIsLoading(true);
    const params = { limit: 100, page: 1 };
    if (filters.session_id) params.session_id = filters.session_id;
    if (filters.term_id) params.term_id = filters.term_id;
    if (filters.class_id) params.class_id = filters.class_id;
    httprequest.get(`${SYLLABUS_ENDPOINT}/subjects/${subjectId}`, { params })
      .then((res) => {
        const items = res?.data?.data?.items || [];
        setSearchResult(items);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.message);
      });
  };

  useEffect(() => {
    getSubject();
    getSessions();
    getTerms();
    getClasses();
    getSyllabusBaseline();
  }, [subjectId]);

  useEffect(() => {
    getSyllabusList();
  }, [subjectId, filters.session_id, filters.term_id, filters.class_id]);

  const termsForCreate = allTerms.filter((term) => !createForm.session_id || term?.session_id === createForm.session_id);
  const filteredClassList = classList.filter((c) => c?.class_name?.toLowerCase().includes(classSearch.toLowerCase()));

  const handleOpenCreateModal = () => setIsCreateModalOpen(true);

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateForm({ session_id: "", term_id: "", class_id: [] });
    setClassSearch("");
  };

  const toggleCreateClass = (classId) => {
    setCreateForm((prev) => ({
      ...prev,
      class_id: prev.class_id.includes(classId)
        ? prev.class_id.filter((id) => id !== classId)
        : [...prev.class_id, classId]
    }));
  };

  const handleCreateSyllabus = () => {
    if (createValidator.allValid()) {
      setIsCreating(true);
      const session = sessions.find((s) => s.id === createForm.session_id);
      const term = allTerms.find((t) => t.id === createForm.term_id);
      const payload = {
        title: `${subjectName} ${session?.session || ""} Session - ${term?.term || ""}`,
        subject_id: subjectId,
        term_id: createForm.term_id,
        session_id: createForm.session_id,
        classes: createForm.class_id,
        status: "DRAFT"
      };
      httprequest.post(SYLLABUS_ENDPOINT, payload)
        .then((res) => {
          setIsCreating(false);
          toast.success(res?.data?.message || "Syllabus created successfully");
          handleCloseCreateModal();
          getSyllabusBaseline();
          getSyllabusList();
        })
        .catch((err) => {
          setIsCreating(false);
          toast.error(err?.response?.data?.message);
        });
    } else {
      showCreateValidator(true);
    }
  };

  const columns = [
    {
      name: "Session",
      selector: (row) => row?.session?.name
    },
    {
      name: "Term",
      selector: (row) => row?.term?.name
    },
    {
      name: "Classes",
      selector: (row) => {
        const names = row?.classes?.map((c) => c?.class_name) || [];

        return names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", ");
      }
    },
    {
      name: "Date Created",
      selector: (row) => row?.created_at && new Date(row.created_at).toLocaleDateString()
    },
    {
      name: "Date Updated",
      selector: (row) => row?.updated_at && new Date(row.updated_at).toLocaleDateString()
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <button
          className="table-action-icon"
          onClick={() => navigate(`/class-activities/employer/syllabus/${subjectId}/${row?.id}`)}
        >
          <Icon icon="mdi:chevron-right" />
        </button>
      )
    }
  ];

  return (
    <Layout
      children={
        <>
          <BreadCrumb
            link_one="/class-activities/subjects"
            link_one_name="All Subjects"
            active="Syllabus"
            description="View and manage syllabus content for this subject"
          />

          <PageTitle
            title={subjectName || "Syllabus"}
            button={
              <Button
                buttonName="Create Syllabus"
                buttonIcon={<Icon icon="mdi:plus" style={{ marginRight: 6 }} />}
                type="button"
                variant="primary"
                color="btndefault"
                buttonSize="fluid"
                onClick={handleOpenCreateModal}
              />
            }
          />

          <Grid container spacing={3} mt={1}>
            <Grid item xs={12}>
              {!isLoading && !hasAnySyllabus ? (
                <div className={`${classes.emptyStateContainer} table-container page-content white_bg`}>
                  <div className={classes.emptyStateIcon}>
                    <Icon icon="ph:book-open-text-light" />
                  </div>
                  <h3 className={classes.emptyStateTitle}>No Syllabus Yet</h3>
                  <p className={classes.emptyStateSubtitle}>
                    Create your first syllabus and assign it to one or more classes. Teachers and
                    students will be able to access it once it&apos;s published.
                  </p>
                  <Button
                    buttonName="Create Syllabus"
                    buttonIcon={<Icon icon="mdi:plus" style={{ marginRight: 6 }} />}
                    type="button"
                    variant="primary"
                    color="btndefault"
                    buttonSize="fluid"
                    onClick={handleOpenCreateModal}
                  />
                </div>
              ) : (
                <div className="page-content">
                  <Table
                    data={searchResult}
                    columns={columns}
                    progressPending={isLoading}
                    pagination={true}
                    paginationPerPage={10}
                  />
                </div>
              )}
            </Grid>
          </Grid>

          <Modal
            title="Create Syllabus"
            subtitle={<div>A syllabus is created once per subject and assigned to one or more classes.</div>}
            modalContent={
              <>
                <Grid container spacing={2} className="input-padding">
                  <Grid item xs={12} sm={6}>
                    <SelectGroup label="Session" required>
                      <select
                        value={createForm.session_id}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, session_id: e.target.value, term_id: "" }))}
                      >
                        <option value="">Select Session</option>
                        {sessions.map((s) => (
                          <option key={s?.id} value={s?.id}>{s?.session}</option>
                        ))}
                      </select>
                    </SelectGroup>
                    {createValidator.message("Session", createForm.session_id, "required")}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SelectGroup label="Term" required>
                      <select
                        value={createForm.term_id}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, term_id: e.target.value }))}
                      >
                        <option value="">Select Term</option>
                        {termsForCreate.map((t) => (
                          <option key={t?.id} value={t?.id}>{t?.term}</option>
                        ))}
                      </select>
                    </SelectGroup>
                    {createValidator.message("Term", createForm.term_id, "required")}
                  </Grid>
                  <Grid item xs={12}>
                    <SelectGroup label="Select Class (Select one or more)" required>
                      <input
                        type="search"
                        placeholder="Search class"
                        value={classSearch}
                        onChange={(e) => setClassSearch(e.target.value)}
                        className="table_search"
                        style={{ marginBottom: 8, width: "100%" }}
                      />
                      <div className={classes.checkboxList}>
                        {filteredClassList.map((c) => (
                          <label key={c?.id} className={classes.checkboxItem}>
                            <input
                              type="checkbox"
                              checked={createForm.class_id.includes(c?.id)}
                              onChange={() => toggleCreateClass(c?.id)}
                            />
                            {c?.class_name}
                          </label>
                        ))}
                      </div>
                    </SelectGroup>
                    {createValidator.message("Class", createForm.class_id.length, "required|min:1,num")}
                  </Grid>
                </Grid>
              </>
            }
            modalFooter={
              <div className={classes.modalFooterBtn}>
                <Button
                  buttonName="Cancel"
                  onClick={handleCloseCreateModal}
                  variant="primaryoutline"
                  color="btnfontprimary"
                  buttonSize="fluid"
                />
                <Button
                  buttonName="Create Syllabus"
                  variant="primary"
                  color="btndefault"
                  buttonSize="fluid"
                  onClick={handleCreateSyllabus}
                  isLoading={isCreating}
                />
              </div>
            }
            isopen={isCreateModalOpen}
            closeModal={handleCloseCreateModal}
          />
        </>
      }
    />
  );
};

export default EmployerSyllabusDetails;
